import React from 'react';
import type { RoutesPaths } from '@shared/core';
import { RoutesProvider } from '@shared/core';
import globalWindow from '@shared/core/globals';

/**
 * Example Route name types. Defines the routes that are available
 */
type RoutesNames =
  | 'account'
  | 'accommodations'
  | 'card'
  | 'cashfunds'
  | 'checklist'
  | 'collectContacts'
  | 'contact'
  | 'customPage'
  | 'dashboard'
  | 'design'
  | 'domain'
  | 'ecard'
  | 'event'
  | 'eventServiceCenter'
  | 'faq'
  | 'accommodationsStayAreas'
  | 'guestHotelBookings'
  | 'guestList'
  | 'guests'
  | 'home'
  | 'hotelBlock'
  | 'hotelReminder'
  | 'hotelSearch'
  | 'ideas'
  | 'inspiration'
  | 'invitations'
  | 'emailTemplates'
  | 'message'
  | 'messaging'
  | 'messagingDrafts'
  | 'photoCollection'
  | 'photos'
  | 'planning'
  | 'planningAccommodations'
  | 'postOffice'
  | 'proschedule'
  | 'provip'
  | 'registration'
  | 'registry'
  | 'registryLinks'
  | 'registryManage'
  | 'registryTrack'
  | 'rsvp'
  | 'rsvpReminder'
  | 'savethedate'
  | 'schedule'
  | 'settings'
  | 'settingsGeneral'
  | 'settingsMenu'
  | 'settingsSecurity'
  | 'shop'
  | 'story'
  | 'textBlast'
  | 'travel'
  | 'vendorShowcase'
  | 'video'
  | 'vip'
  | 'unifiedWebsiteEditor'
  | 'unifiedWebsiteEditorPages'
  | 'website'
  | 'websiteOverview'
  | 'websiteSettings'
  | 'welcome'
  | 'whereToStaySettings';

/**
 * Example route paths. Has a path for the <Router /> and a function for history to push to.
 */
export const routePaths: RoutesPaths<RoutesNames> = {
  home: {
    path: '',
    goToPath: () => '',
  },
  cashfunds: {
    path: 'cash-fund',
    goToPath: () => 'cash-fund',
  },
  dashboard: {
    path: 'dashboard',
    goToPath: () => 'dashboard',
  },
  design: {
    path: 'design',
    goToPath: () => 'design',
  },
  checklist: {
    path: 'checklist',
    goToPath: () => 'checklist',
  },
  vendorShowcase: {
    path: 'showcase',
    goToPath: () => 'showcase',
  },
  guests: {
    path: 'guests',
    goToPath: () => 'guests',
  },
  invitations: {
    path: 'invitations',
    goToPath: () => 'invitations',
  },
  photos: {
    path: 'photos',
    goToPath: () => 'photos',
  },
  rsvp: {
    path: 'rsvp',
    goToPath: () => 'rsvp',
  },
  registry: {
    path: 'registry',
    goToPath: () => 'registry',
  },
  registryManage: {
    path: 'registry/manage',
    goToPath: () => 'registry/manage',
  },
  registryTrack: {
    path: 'registry/track',
    goToPath: () => 'registry/track',
  },
  inspiration: {
    path: 'inspiration',
    goToPath: () => 'inspiration',
  },
  settings: {
    path: 'settings',
    goToPath: () => 'settings',
  },
  account: {
    path: 'settings/account',
    goToPath: () => 'settings/account',
  },
  settingsSecurity: {
    path: 'settings/security',
    goToPath: () => 'settings/security',
  },
  settingsGeneral: {
    path: 'settings/general',
    goToPath: (section?: string) => (section ? `settings/general#${section}` : 'settings/general'),
  },
  whereToStaySettings: {
    path: 'settings/where-to-stay',
    goToPath: () => 'settings/where-to-stay',
  },
  websiteSettings: {
    path: 'settings/website',
    goToPath: () => 'settings/website',
  },
  settingsMenu: {
    path: 'settings/menu',
    goToPath: () => 'settings/menu',
  },
  video: {
    path: 'video',
    goToPath: () => 'video',
  },
  domain: {
    path: 'domain',
    goToPath: () => 'domain',
  },
  ideas: {
    path: 'ideas',
    goToPath: () => 'ideas',
  },
  postOffice: {
    path: 'email',
    goToPath: () => 'email',
  },
  story: {
    path: 'story',
    goToPath: () => 'story',
  },
  textBlast: {
    path: 'text-blast',
    goToPath: () => 'text-blast',
  },
  schedule: {
    path: 'schedule',
    goToPath: () => 'schedule',
  },
  travel: {
    path: 'travel',
    goToPath: () => 'travel',
  },
  accommodations: {
    path: 'accommodations',
    goToPath: () => 'accommodations',
  },
  guestHotelBookings: {
    path: 'accommodations/guests',
    goToPath: () => 'accommodations/guests',
  },
  accommodationsStayAreas: {
    path: 'accommodations/stay-areas',
    goToPath: () => 'accommodations/stay-areas',
  },
  website: {
    path: 'website/:pageSlug',
    goToPath: (pageSlug: string) => `website/${pageSlug}`,
  },
  unifiedWebsiteEditor: {
    path: 'website',
    goToPath: () => 'website',
  },
  unifiedWebsiteEditorPages: {
    path: 'website/pages',
    goToPath: () => 'website/pages',
  },
  customPage: {
    path: 'website/page/:pageSlug',
    goToPath: (pageSlug: string) => `website/page/${pageSlug}`,
  },
  registryLinks: {
    path: 'registrylinks',
    goToPath: () => 'registrylinks',
  },
  vip: {
    path: 'vip',
    goToPath: () => 'vip',
  },
  faq: {
    path: 'faq',
    goToPath: () => 'faq',
  },
  welcome: {
    path: 'welcome',
    goToPath: () => 'welcome',
  },
  shop: {
    path: 'registry/shop',
    goToPath: () => 'registry/shop',
  },
  ecard: {
    path: 'ecard/:draftType',
    goToPath: (draftType: string) => `ecard/${draftType}`,
  },
  card: {
    path: 'cards',
    goToPath: () => `cards`,
  },
  savethedate: {
    path: 'savethedate',
    goToPath: () => 'savethedate',
  },
  eventServiceCenter: {
    path: 'newservicecenter',
    goToPath: () => 'newservicecenter',
  },
  planning: {
    path: 'planning',
    goToPath: () => 'planning',
  },
  proschedule: {
    path: 'proschedule',
    goToPath: () => 'proschedule',
  },
  provip: {
    path: 'provip',
    goToPath: () => 'provip',
  },
  hotelBlock: {
    path: 'planning/hotelblock',
    goToPath: () => 'planning/hotelblock',
  },
  hotelSearch: {
    path: 'hotel',
    goToPath: () => 'hotel',
  },
  planningAccommodations: {
    path: 'planning/accommodations',
    goToPath: () => 'planning/accommodations',
  },
  collectContacts: {
    path: 'collect-contacts',
    goToPath: () => 'collect-contacts',
  },
  contact: {
    path: 'contact',
    goToPath: () => 'contact',
  },
  hotelReminder: {
    path: 'hotel-reminder',
    goToPath: () => 'hotel-reminder',
  },
  rsvpReminder: {
    path: 'send-rsvp-reminder',
    goToPath: () => 'send-rsvp-reminder',
  },
  event: {
    path: 'event',
    goToPath: () => 'event',
  },
  websiteOverview: {
    path: 'website/overview',
    goToPath: () => 'website/overview',
  },
  photoCollection: {
    path: 'website/photos',
    goToPath: () => 'website/photos',
  },
  messaging: {
    path: 'messaging',
    goToPath: () => 'messaging',
  },
  emailTemplates: {
    path: 'messaging/templates',
    goToPath: () => 'messaging/templates',
  },
  messagingDrafts: {
    path: 'messaging/drafts',
    goToPath: () => 'messaging/drafts',
  },
  message: {
    path: 'message/:type',
    goToPath: (type: string) => `message/${type}`,
  },
  guestList: {
    path: 'guestlist',
    goToPath: () => 'guestlist',
  },
  registration: {
    path: 'registration',
    goToPath: () => 'registration',
  },
};

export const Router: React.FC<{ eventHandle: string }> = ({ eventHandle, children }) => {
  return <RoutesProvider handle={eventHandle}>{children}</RoutesProvider>;
};

export const monorepoRoutePaths = {
  accountPersonalInfo: 'account/personal-info',
  accountVerifyEmail: `/account/verify-email?prev=${globalWindow.location?.href}?dialog=verifyEmail`,
  eCardChangeTheDate: 'ecard/changethedate',
  emailCreate: 'email/create',
  eCardCustom: 'ecard/custom',
  eCardInvitations: 'ecard/invitation',
  eCardSaveTheDate: 'ecard/savethedate',
  faq: 'faq',
  guests: 'guests',
  welcome: 'welcome',
  photos: 'photos',
  registryShop: 'registry/shop',
  registryOverview: 'registry',
  registryManage: 'registry/manage',
  registryTrack: 'registry/track',
  rsvp: 'rsvp',
  rsvpSettings: 'rsvp/settings',
  schedule: 'schedule',
  settings: 'settings',
  settingsAccount: 'settings/account',
  settingsGeneral: 'settings/general',
  settingsMenu: 'settings/menu',
  settingsSecurity: 'settings/security',
  story: 'story',
  travel: 'travel',
  video: 'video',
  vip: 'vip',
  writersblock: 'writersblock',
};
